import * as React from 'react'
import { Text } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import ControleDetailComponent from './ControleDetailComponent'
import SimpleAlert from '@components/modal/SimpleAlert'
import Loader from '@components/loader/Loader'
import Modal from '@components/modal/Modal'

import api from '@api/api'
import logger from '@utils/logger'
import exporter from '@utils/exporter'
import config from '@app/config'

import useReducer from '@store/useReducer'
import * as ControleStore from '@store/controle/controle'
import * as AuthStore from '@store/auth/auth'

import useI18n from '@i18n/useI18n'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'

interface Props {
  id: string

  // origin
  manifestationId?: string
  societeId?: string
  espaceId?: string
}

const ControleScreen = ({ id, manifestationId, societeId, espaceId }: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const isPhone = useBreakpoint()[1] === 'phone'

  const [status, setStatus] = React.useState<Status>('loading')
  const [controle, setControle] = React.useState<ControleQualiteResponse>()
  const [exporting, setExporting] = React.useState<'PDF' | 'XLSX'>()

  const controles = useReducer(ControleStore.store, (s) => s.controles)

  const fetchFullControle = () => {
    setStatus('loading')
    const stored = controles.find((c) => c.id === id)

    if (stored) {
      setControle(stored)
      setStatus('fetched')
    } else {
      return api.controle
        .get(id)
        .then((res) => {
          setControle(res)
          setStatus('fetched')
        })
        .catch((err) => {
          logger.error(err)
          setStatus('error')
        })
    }
  }

  React.useEffect(() => {
    fetchFullControle()
  }, [id])

  React.useEffect(() => {
    if (!!exporting && !!controle) {
      const { tokens } = AuthStore.store.getState()

      if (tokens) {
        if (isPhone) {
          Modal.open({ Content: () => <Loader />, position: 'center' })
        }

        const exportAPI =
          exporting === 'PDF'
            ? exporter.download<IncidentFilterRequest>(
                config.ws.controle.exportPDF.replace('{controleQualiteId}', controle.id),
                'export_controle_qualite.pdf',
                'application/pdf'
              )
            : exporter.download<IncidentFilterRequest>(
                config.ws.controle.exportXLSX.replace('{controleQualiteId}', controle.id),
                'export_controle_qualite.xlsx',
                'application/vnd.ms-excel'
              )

        exportAPI
          .then(() => {
            if (isPhone) {
              SimpleAlert.open({
                title: i18n.t('screens.controle.export.success.title'),
                description: i18n.t('screens.controle.export.success.description'),
                buttons: [{ label: i18n.t('screens.controle.export.success.ok'), onClick: () => SimpleAlert.close() }],
              })
            }
          })
          .catch((error) => {
            logger.error('Unable to download', error)
            SimpleAlert.open({
              title: i18n.t('screens.controle.export.error.title'),
              description: i18n.t('screens.controle.export.error.description'),
              buttons: [{ label: i18n.t('screens.controle.export.error.ok'), onClick: () => SimpleAlert.close() }],
            })
          })
          .finally(() => {
            if (isPhone) {
              Modal.close()
            }
            setExporting(undefined)
          })
      }
    }
  }, [exporting, controle])

  const download = () => {
    SimpleAlert.open({
      title: i18n.t('screens.controle.export.title'),
      description: i18n.t('screens.controle.export.description'),
      buttons: [
        {
          label: i18n.t('screens.controle.export.pdf'),
          onClick: () => {
            SimpleAlert.close()
            setExporting('PDF')
          },
          disabled: !!exporting,
        },
        {
          label: i18n.t('screens.controle.export.xls'),
          onClick: () => {
            SimpleAlert.close()
            setExporting('XLSX')
          },
          disabled: !!exporting,
        },
      ],
    })
  }

  const buttons: HeaderButton[] = [
    { label: i18n.t('common.download'), onClick: download, status: 'active', leftIcon: 'download' },
  ]

  return (
    <Root
      loading={status === 'loading'}
      header={
        controle ? i18n.t('screens.controle.title', { numero: controle.numero }) : i18n.t('screens.controles.title')
      }
      onLeft={navigation.goBack}
      onRight={buttons[0].onClick}
      right="download"
      fixed>
      {controle ? (
        <ControleDetailComponent
          controle={controle}
          buttons={buttons}
          espaceId={espaceId}
          manifestationId={manifestationId}
          societeId={societeId}
        />
      ) : (
        <MissingInfo>{i18n.t('common.error')}</MissingInfo>
      )}
    </Root>
  )
}

export default ControleScreen

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`
